import { useState, useEffect, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, Clock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import emailjs from "@emailjs/browser";
import { useAuth } from "@/context/AuthContext";
import {
  getGoogleBusy,
  getMyMonthMinutes,
  createBooking,
  MONTHLY_LIMIT_MIN,
  SLOT_MINUTES,
} from "@/lib/booking";

const EMAILJS_SERVICE_ID = "service_t060r7s";
const EMAILJS_TEMPLATE_ID = "template_zw256co";
const EMAILJS_PUBLIC_KEY = "Uk8xcFa3VjvjRAgAQ";

// Finestra di disponibilità del mentor, in orario CIVILE di Europe/Rome
// (modificabile qui). Ogni visitatore la vede convertita nel proprio fuso
// orario locale del browser (vedi zonedTimeToUtc più sotto).
//   OPEN_HOUR  = prima ora prenotabile (inizio slot)
//   CLOSE_HOUR = ultima ora prenotabile (inizio slot)
//   AVAILABLE_DAYS = giorni prenotabili (0=Dom, 1=Lun ... 6=Sab)
// Google Calendar rimuove poi gli slot già occupati.
const MENTOR_TIMEZONE = "Europe/Rome";
const OPEN_HOUR = 10;
const CLOSE_HOUR = 22;
const AVAILABLE_DAYS = [1, 2, 3, 4, 5]; // Lun-Ven (chiuso sabato e domenica)
// Pausa di chiusura (in minuti dalla mezzanotte, orario di Roma): 16:30 -> 20:30.
// Gli slot che si sovrappongono a questa fascia vengono esclusi.
const BREAK_START_MIN = 16 * 60 + 30;
const BREAK_END_MIN = 20 * 60 + 30;

// Converte un orario civile (wall-clock) in uno specifico fuso orario in un
// istante UTC assoluto, gestendo automaticamente ora legale/solare. Senza
// questo, un orario tipo "10:00" verrebbe interpretato nel fuso orario del
// browser di chi visita la pagina invece che in quello del mentor (Europe/Rome),
// facendo risultare bloccati/liberi gli slot sbagliati per chi non è in Italia.
function zonedTimeToUtc(timeZone, year, month, day, hour, minute) {
  const guess = Date.UTC(year, month, day, hour, minute, 0, 0);
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const parts = Object.fromEntries(dtf.formatToParts(new Date(guess)).map((p) => [p.type, p.value]));
  const renderedAsUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour) % 24, Number(parts.minute), Number(parts.second),
  );
  return new Date(guess - (renderedAsUtc - guess));
}

const HOURS = [];
for (let h = OPEN_HOUR; h <= CLOSE_HOUR; h++) {
  const slotStart = h * 60;
  const slotEnd = slotStart + SLOT_MINUTES;
  // Escludi se lo slot si sovrappone alla pausa
  if (slotStart < BREAK_END_MIN && slotEnd > BREAK_START_MIN) continue;
  HOURS.push(`${String(h).padStart(2, "0")}:00`);
}
const WEEKLY_SLOTS = AVAILABLE_DAYS.reduce((acc, day) => {
  acc[day] = HOURS;
  return acc;
}, {});

// Periodi di chiusura (ferie, ecc.) — date incluse, formato "YYYY-MM-DD".
const BLOCKED_RANGES = [
  { from: "2026-07-23", to: "2026-07-31" },
];

function isBlockedDate(date) {
  const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return BLOCKED_RANGES.some((r) => ymd >= r.from && ymd <= r.to);
}

const DAY_NAMES = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function slotKey(date, time) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${time}`;
}

export default function BookingCalendar() {
  const { user, profile } = useAuth();
  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selectedDay, setSelectedDay] = useState(null);
  const [busy, setBusy] = useState([]); // [{ start: Date, end: Date }]
  const [usedMinutes, setUsedMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: "success"|"error", msg }
  const [pendingTime, setPendingTime] = useState(null); // orario in attesa di conferma

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const [busyRaw, mine] = await Promise.all([
        getGoogleBusy(view.year, view.month),
        user ? getMyMonthMinutes(user.id, view.year, view.month) : Promise.resolve(0),
      ]);
      setBusy(busyRaw.map((b) => ({ start: new Date(b.start), end: new Date(b.end) })));
      setUsedMinutes(mine);
    } catch {
      setBusy([]);
    } finally {
      setLoading(false);
    }
  }, [view.year, view.month, user]);

  // Uno slot è occupato se si sovrappone a un intervallo busy del calendario
  const isSlotBusy = (utcStart) => {
    const utcEnd = new Date(utcStart.getTime() + SLOT_MINUTES * 60000);
    return busy.some((b) => utcStart < b.end && utcEnd > b.start);
  };

  useEffect(() => { loadMonth(); }, [loadMonth]);

  const remaining = MONTHLY_LIMIT_MIN - usedMinutes;
  const canBook = remaining >= SLOT_MINUTES;

  // Costruisce le celle del mese (incluse celle vuote iniziali per allineare la griglia)
  const firstDay = new Date(view.year, view.month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // settimana che parte da Lunedì
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.year, view.month, d));

  const isPast = (date) => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return date < t;
  };
  const dayHasSlots = (date) => !isPast(date) && !isBlockedDate(date) && (WEEKLY_SLOTS[date.getDay()]?.length > 0);

  const changeMonth = (delta) => {
    setSelectedDay(null);
    setFeedback(null);
    const m = view.month + delta;
    setView({ year: view.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 });
  };

  // Orari del mentor (Europe/Rome) per il giorno selezionato, convertiti in
  // istanti UTC assoluti e poi mostrati nel fuso orario locale di chi visita.
  const slotsForSelected = useMemo(() => {
    if (!selectedDay) return [];
    const romeHours = WEEKLY_SLOTS[selectedDay.getDay()] || [];
    return romeHours.map((romeTime) => {
      const [h, m] = romeTime.split(":").map(Number);
      const utcStart = zonedTimeToUtc(
        MENTOR_TIMEZONE, selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate(), h, m,
      );
      return {
        romeTime,
        utcStart,
        localLabel: utcStart.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
    });
  }, [selectedDay]);

  const handleBook = async (slot) => {
    if (!canBook || booking) return;
    setPendingTime(null);
    setBooking(true);
    setFeedback(null);
    try {
      await createBooking(slot.utcStart.toISOString());
      // Notifica di prenotazione via EmailJS (non blocca la conferma se fallisce).
      // Sempre in orario di Roma: la mail è per il mentor, non per il cliente.
      const when = new Intl.DateTimeFormat("it-IT", {
        timeZone: MENTOR_TIMEZONE,
        weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      }).format(slot.utcStart);
      emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: profile?.full_name || "Cliente",
          email: profile?.email || user?.email || "",
          service: "Prenotazione Mentorship 1to1",
          message: `Nuova sessione prenotata per: ${when} (durata ${SLOT_MINUTES} min).`,
        },
        { publicKey: EMAILJS_PUBLIC_KEY },
      ).catch(() => {});
      setFeedback({ type: "success", msg: "Prenotazione confermata! La trovi sul calendario." });
      await loadMonth();
    } catch (e) {
      setFeedback({ type: "error", msg: e?.message || "Errore durante la prenotazione. Riprova." });
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Prenota la tua sessione
        </h1>
        <p className="text-slate-400 text-sm">Scegli un giorno e un orario disponibile per la tua mentorship 1to1.</p>
      </div>

      {/* Quota banner */}
      <div className={`mb-6 flex items-center gap-3 rounded-xl px-4 py-3 border ${
        canBook ? "border-violet-500/20 bg-violet-500/10" : "border-amber-500/20 bg-amber-500/10"
      }`}>
        <Clock size={16} className={canBook ? "text-violet-400" : "text-amber-400"} />
        <p className="text-sm text-slate-300">
          Hai usato <strong>{usedMinutes} min</strong> su <strong>{MONTHLY_LIMIT_MIN} min</strong> disponibili questo mese
          {!canBook && " — limite raggiunto."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* Calendario */}
        <div className="bg-[#111113] border border-[#1E1E2A] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {MONTH_NAMES[view.month]} {view.year}
            </h2>
            <div className="flex gap-1">
              <button onClick={() => changeMonth(-1)} className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => changeMonth(1)} className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Intestazione giorni */}
          <div className="grid grid-cols-7 gap-1.5 mb-1.5">
            {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-slate-600 uppercase tracking-wide py-1">{d}</div>
            ))}
          </div>

          {loading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="animate-spin text-violet-400" size={22} />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1.5">
              {cells.map((date, i) => {
                if (!date) return <div key={`e${i}`} />;
                const available = dayHasSlots(date);
                const selected = selectedDay && date.toDateString() === selectedDay.toDateString();
                const isToday = date.toDateString() === today.toDateString();
                return (
                  <button
                    key={slotKey(date, "d")}
                    disabled={!available}
                    onClick={() => { setSelectedDay(date); setFeedback(null); }}
                    className={`aspect-square rounded-xl border flex flex-col items-center justify-center transition-all ${
                      selected
                        ? "border-violet-500 bg-violet-500/15"
                        : available
                        ? "border-[#1E1E2A] bg-[#16161A] hover:border-violet-500/40 hover:bg-violet-500/5 cursor-pointer"
                        : "border-transparent bg-transparent opacity-30 cursor-not-allowed"
                    }`}
                  >
                    <span className={`text-[10px] uppercase tracking-wide ${available ? "text-slate-500" : "text-slate-700"}`}>
                      {DAY_NAMES[date.getDay()]}
                    </span>
                    <span className={`text-base font-semibold ${
                      selected ? "text-violet-300" : isToday ? "text-violet-400" : available ? "text-white" : "text-slate-700"
                    }`}>
                      {date.getDate()}
                    </span>
                    {available && <span className="w-1 h-1 rounded-full bg-emerald-400 mt-0.5" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Slot orari */}
        <div className="bg-[#111113] border border-[#1E1E2A] rounded-2xl p-5">
          {!selectedDay ? (
            <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-center">
              <Clock size={22} className="text-slate-600 mb-2" />
              <p className="text-sm text-slate-500">Seleziona un giorno per vedere gli orari disponibili.</p>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {DAY_NAMES[selectedDay.getDay()]} {selectedDay.getDate()} {MONTH_NAMES[selectedDay.getMonth()]}
              </p>
              <div className="space-y-2">
                {slotsForSelected.map((slot) => {
                  const booked = isSlotBusy(slot.utcStart);
                  const disabled = booked || !canBook || booking;
                  return (
                    <button
                      key={slot.romeTime}
                      disabled={disabled}
                      onClick={() => setPendingTime(slot)}
                      className={`w-full py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        booked
                          ? "border-[#1E1E2A] text-slate-600 line-through cursor-not-allowed"
                          : disabled
                          ? "border-[#1E1E2A] text-slate-600 cursor-not-allowed"
                          : "border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
                      }`}
                    >
                      {slot.localLabel}{booked && " · occupato"}
                    </button>
                  );
                })}
              </div>

              {feedback && (
                <div className={`mt-4 flex items-start gap-2 text-sm ${feedback.type === "success" ? "text-emerald-400" : "text-red-400"}`}>
                  {feedback.type === "success" ? <CheckCircle2 size={15} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />}
                  {feedback.msg}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {pendingTime && selectedDay && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-[#111113] border border-[#1E1E2A] rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-base font-semibold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Confermi la prenotazione?
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              Stai per fissare una call per{" "}
              <strong className="text-slate-200">
                {pendingTime.utcStart.toLocaleDateString([], { weekday: "long", day: "2-digit", month: "long" })}
              </strong>{" "}
              alle <strong className="text-slate-200">{pendingTime.localLabel}</strong> (ora locale).
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingTime(null)}
                disabled={booking}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium border border-[#1E1E2A] text-slate-300 hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={() => handleBook(pendingTime)}
                disabled={booking}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {booking && <Loader2 size={14} className="animate-spin" />}
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
